import { CustomerWallet } from 'types/customers'
import { WithPaginationApiResponseExtended } from 'types/pagination'
import { Transaction } from 'types/transaction'
import { Paging } from 'types/types'

export const FilterModes = {
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
} as const

export type FilterMode = (typeof FilterModes)[keyof typeof FilterModes]

export const LedgerWalletStatuses = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  TERMINATED: 'TERMINATED',
} as const

export type LedgerWalletStatus = (typeof LedgerWalletStatuses)[keyof typeof LedgerWalletStatuses]

export interface LedgerWalletTransactionsRequestParams {
  walletReference?: string
  size?: number
  page?: number
  start?: string
  end?: string
  statuses?: TransactionStatus[]
  filterMode?: FilterMode
  categories?: TransactionCategory[]
}

export interface LedgerWalletsRequestParams {
  currencyCode?: string
  includeCustomerWallets?: boolean
  searchTerm?: string
  size?: number
  page?: number
  start?: string
  end?: string
  statuses?: LedgerWalletStatus[]
}

export interface Amount {
  value: number
  currencyCode: string
}

export const EntityTypes = {
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
} as const

export type EntityType = (typeof EntityTypes)[keyof typeof EntityTypes]

export interface Entity {
  type: EntityType
  name?: string
  firstName?: string
  lastName?: string
}

export const AccountNumberFormats = {
  IBAN: 'IBAN',
  SCAN: 'SCAN',
  SWIFT: 'SWIFT',
  ABA: 'ABA',
  BBAN: 'BBAN',
  UNKNOWN: 'UNKNOWN',
} as const

export type AccountNumberFormat = (typeof AccountNumberFormats)[keyof typeof AccountNumberFormats]

export interface BankAccount {
  accountNumberFormat: AccountNumberFormat
  accountNumber: string
  bankCode: string
  bankName?: string
}

export interface CryptoAddresses {
  addresses: string[]
  tag?: string
  protocol: string
  network: string
}

export interface TransactionParty {
  amount?: Amount
  entity: Entity
  // CRYPTO
  cryptoAddresses?: CryptoAddresses
  // FIAT
  bankAccount?: BankAccount // Present in fiat transactions
}

export interface TransactionDetails {
  // CRYPTO
  exchangeRate?: number
  hash?: string
  // FIAT
  paymentMethod?: string
}

export const TransactionTypes = {
  // FIAT
  PAYOUT: 'PAYOUT',
  PROCESSING_FEE: 'processingFee',
  PAYIN: 'PAYIN',
  FEE: 'FEE',
  ADJUSTMENT: 'ADJUSTMENT',
  WITHDRAWAL_FAILED: 'withdrawalFailed',
  // CRYPTO
  NETWORK_FEE: 'networkFee',
  PAYMENT_OUT: 'paymentOut',
  PAYMENT_IN: 'paymentIn',
  REWARDS: 'rewards',
  // REWARDS / EARN
  REWARDS_DEPOSIT: 'rewardsDeposit',
  REWARDS_WITHDRAWAL: 'rewardsWithdrawal',
  REWARDS_YIELD: 'rewardsYield',
  REWARDS_EARNED: 'rewardsEarned',
  // CHANNEL
  CHANNEL_DEPOSIT: 'channelDeposit',
} as const

export type TransactionType = (typeof TransactionTypes)[keyof typeof TransactionTypes]

export const TransactionTypeMapping: Record<string, string> = {
  PAYOUT: 'userWithdrawal',
  PAYIN: 'userDeposit',
  FEE: 'processingFee',
  processingFee: 'processingFee',
  ADJUSTMENT: 'adjustment',
  withdrawalFailed: 'withdrawalFailed',
  paymentOut: 'paymentOut',
  paymentIn: 'paymentIn',
  networkFee: 'networkFee',
  REWARDS: 'rewards',
}

export const TransactionSubTypeMapping: Record<string, string> = {
  PAYOUT: 'fiatWithdrawal',
  PAYIN: 'fiatDeposit',
  FEE: 'paymentIn',
  processingFee: 'paymentIn',
  ADJUSTMENT: 'adjustment',
  withdrawalFailed: 'fiatWithdrawal',
  paymentOut: 'paymentOut',
  paymentIn: 'paymentIn',
  networkFee: 'networkFee',
}

export const FiatTransactionTypes = {
  PAYOUT: 'PAYOUT',
  PAYIN: 'PAYIN',
  FEE: 'FEE',
  PROCESSING_FEE: 'processingFee',
  ADJUSTMENT: 'ADJUSTMENT',
  WITHDRAWAL_FAILED: 'withdrawalFailed',
} as const

export type FiatTransactionType = (typeof FiatTransactionTypes)[keyof typeof FiatTransactionTypes]

export const TransactionStatuses = {
  CREATED: 'CREATED',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  ON_HOLD: 'ON_HOLD',
  RETURNED: 'RETURNED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  FAILED: 'FAILED',
} as const

export type TransactionStatus = (typeof TransactionStatuses)[keyof typeof TransactionStatuses]

export const PaymentMethods = {
  // EUR
  SEPA_CT: 'SEPA_CT',
  SEPA_INST: 'SEPA_INST',
  // GBP
  FASTER_PAYMENT: 'FASTER_PAYMENT',
  CHAPS: 'CHAPS',
  // USD
  ACH: 'ACH',
  ACH_SAME_DAY: 'ACH_SAME_DAY',
  FEDWIRE: 'FEDWIRE',
  FEDNOW: 'FEDNOW',
  CRNOW: 'CRNOW',
  RTP: 'RTP',
  SWIFT: 'SWIFT',
  // Other
  BOOK: 'BOOK',
} as const

export type PaymentMethod = (typeof PaymentMethods)[keyof typeof PaymentMethods]

export const TransactionCategories = {
  PRIMARY: 'PRIMARY',
  EARNING: 'EARNING',
  CUSTOMER: 'CUSTOMER',
} as const

export type TransactionCategory = (typeof TransactionCategories)[keyof typeof TransactionCategories]

export interface LedgerWalletTransaction {
  transactionId: string
  paymentId: string
  paymentReference: string
  type: TransactionType
  status: TransactionStatus
  walletId: string
  walletIsid?: string
  amount: Amount
  runningBalance: Amount
  originator: TransactionParty
  beneficiary: TransactionParty
  details: TransactionDetails
  createdAt: string
  updatedAt?: string
}

export interface LedgerWalletsResponse extends WithPaginationApiResponseExtended<
  CustomerWallet[]
> {}

export interface LedgerWalletTransactionsResponse extends WithPaginationApiResponseExtended<
  LedgerWalletTransaction[]
> {}

export interface LegacyTransactionsResponse {
  transactions: Transaction[]
  paging: Paging
}
